import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Wearly test integration. Code conditions remain controlled by Shopify.
const tiers = [[51,30,'ig430'],[36,25,'xk425'],[21,20,'ksl320'],[11,15,'dgl4y15'],[5,10,'Jwearly10']];const tier = count => tiers.find(row => count >= row[0]) || [0,0,''];
const owned = new Set([...tiers.map(row => row[2]),'WEARLY10','WEARLY15','WEARLY20','WEARLY25','WEARLY30','WEARLY35','Jwearly15','Jwearly20','Jwearly25','Jwearly30','Jwearly35'].map(x=>x.toLowerCase()));
const root = window.Shopify?.routes?.root || '/';
const pending = new Set();
let snapshot = null, running = null, dirty = false, checkoutBusy = false, timer;

async function json(url, options = {}) {
  const response = await fetch(url, {...options, credentials:'same-origin', signal:AbortSignal.timeout(15000)});
  if (!response.ok) throw Error('Varukorgen kunde inte uppdateras. Försök igen.');
  const data = await response.json();
  if (data.status >= 400) throw Error(data.description || 'Rabatten kunde inte uppdateras.');
  return data;
}
async function read() {
  const url = new URL(root + 'cart', location.origin);
  url.searchParams.set('sections','wearly-cart-state');
  url.searchParams.set('_wearly',Date.now());
  const sections = await json(url, {cache:'no-store'});
  const doc = new DOMParser().parseFromString(sections['wearly-cart-state'] || '', 'text/html');
  const el = doc.querySelector('[data-wearly-cart-state]');
  if (!el) throw Error('Rabattkopplingen saknas. Kontrollera filen wearly-cart-state.liquid.');
  const state = JSON.parse(el.textContent);
  if (!Array.isArray(state.cart?.items) || !Number.isInteger(state.count)) throw Error('Varukorgens antal kunde inte läsas.');
  return state;
}
function codes(cart) {
  return [...new Set([
    ...(cart.discount_codes || []).map(x=>x.code),
    ...(cart.cart_level_discount_applications || []).filter(x=>x.type==='discount_code').map(x=>x.title),
    ...cart.items.flatMap(x=>(x.line_level_discount_allocations || []).filter(a=>a.discount_application?.type==='discount_code').map(a=>a.discount_application.title))
  ].filter(Boolean))];
}
function applied(cart, code) {
  return (cart.discount_codes || []).some(x=>x.code?.toLowerCase()===code.toLowerCase() && x.applicable !== false) ||
    (cart.cart_level_discount_applications || []).some(x=>x.type==='discount_code' && x.title?.toLowerCase()===code.toLowerCase() && Number(x.total_allocated_amount)>0) ||
    cart.items.some(x=>(x.line_level_discount_allocations || []).some(a=>a.discount_application?.title?.toLowerCase()===code.toLowerCase() && Number(a.amount)>0));
}
function announce(state) {
  snapshot=state;
  document.dispatchEvent(new CustomEvent('wearly:discount-state',{detail:state}));
}
function message(error) {
  let box=document.getElementById('wearly-discount-error');
  if (!box) {
    box=document.createElement('div');box.id='wearly-discount-error';box.setAttribute('role','alert');
    box.style.cssText='position:fixed;bottom:16px;left:16px;right:16px;z-index:100000;background:#fff3ef;color:#721b12;border:1px solid #c66;padding:16px;font:14px/1.5 sans-serif;box-shadow:0 2px 12px #0002';
    const text=document.createElement('span');text.dataset.errorText='';box.append(text);
    const retry=document.createElement('button');retry.type='button';retry.textContent='Försök igen';retry.style.marginLeft='12px';retry.onclick=()=>sync().catch(message);box.append(retry);document.body.append(box);
  }
  box.hidden=!error;
  box.querySelector('[data-error-text]').textContent=error ? error.message : '';
}
async function reconcile() {
  for(let attempt=0;attempt<5;attempt++) {
    dirty=false;
    await Promise.allSettled([...pending]);
    const before=await read();
    const desired=tier(before.count)[2];
    const existing=codes(before.cart);
    const foreign=existing.filter(x=>!owned.has(x.toLowerCase()));
    const current=existing.filter(x=>owned.has(x.toLowerCase()));
    const correct=desired ? current.length===1 && current[0].toLowerCase()===desired.toLowerCase() && applied(before.cart,desired) : current.length===0;
    if (!correct) {
      const sections=[...new Set([...document.querySelectorAll('cart-items-component')].map(x=>x.dataset.sectionId).filter(Boolean))].slice(0,5);
      const deferred=CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(()=>{});
      const event=new CartLinesUpdateEvent({action:'update',context:'cart',lines:[],promise:deferred.promise});
      event.wearlyDiscount=true;
      document.dispatchEvent(event);
      try {
        const result=await json(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({discount:[...foreign,...(desired?[desired]:[])].join(','),sections,sections_url:location.pathname})});
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(result),detail:{items:result.items,sections:result.sections,source:'wearly-discounts',didError:false}});
      } catch(error) {deferred.reject(error);throw error;}
    }
    const after=await read();
    if(dirty || pending.size || tier(after.count)[2]!==desired) continue;
    if(desired && !applied(after.cart,desired)) throw Error('Mängdrabatten kunde inte bekräftas. Kontrollera rabattkoden och eventuella andra rabatter, eller kontakta oss.');
    if(!desired && codes(after.cart).some(x=>owned.has(x.toLowerCase()))) throw Error('Den tidigare mängdrabatten kunde inte tas bort. Försök igen.');
    announce(after);message(null);return after;
  }
  throw Error('Varukorgen ändras fortfarande. Vänta ett ögonblick och försök igen.');
}
function sync() {
  if(running){dirty=true;return running;}
  running=reconcile().finally(()=>{running=null;});return running;
}
function schedule(){clearTimeout(timer);timer=setTimeout(()=>sync().catch(message),200);}
document.addEventListener(StandardEvents.cartLinesUpdate,event=>{
  if(event.wearlyDiscount)return;
  dirty=true;
  if(event.promise){const p=event.promise;pending.add(p);p.then(()=>{pending.delete(p);schedule();},()=>{pending.delete(p);schedule();});}
  else schedule();
});
// Ordinary checkout waits for the last cart operation and a fresh discount check.
async function checkout() {
  if(checkoutBusy)return;checkoutBusy=true;
  try {await Promise.allSettled([...pending]);if(running)await running;await sync();location.assign(root+'checkout');}
  catch(error){message(error);checkoutBusy=false;}
}
document.addEventListener('click',event=>{
  const el=event.target.closest('button[name="checkout"],input[name="checkout"],a[href]');
  if(!el || el.disabled || event.button!==0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)return;
  const isLink=el.tagName==='A' && new URL(el.href,location.href).origin===location.origin && /\/checkout\/?$/.test(new URL(el.href,location.href).pathname);
  if(el.name!=='checkout' && !isLink)return;
  event.preventDefault();event.stopImmediatePropagation();checkout();
},true);
document.addEventListener('submit',event=>{
  if(event.submitter?.name!=='checkout')return;
  event.preventDefault();event.stopImmediatePropagation();checkout();
},true);
window.WearlyDiscounts={tier,sync,get state(){return snapshot;}};
window.addEventListener('pageshow',schedule);
document.addEventListener('visibilitychange',()=>{if(!document.hidden)schedule();});
schedule();